import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { HiArrowLeft, HiMail, HiCheck, HiShare, HiX } from 'react-icons/hi';
import { FaWhatsapp } from 'react-icons/fa';
import data from '../../data/index.json';
import { getProductImage } from '../../constants/images';
import { Helmet } from 'react-helmet-async';

const ease = [0.22, 1, 0.36, 1];

const ProductDetail = () => {
  const { t } = useTranslation();
  const { productId } = useParams();
  const product = data.products?.find((p) => p.id === parseInt(productId));

  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [mainImage, setMainImage] = useState('');
  const [showContactPopup, setShowContactPopup] = useState(false);
  const navigate = useNavigate();

  const handleBackToStore = () => {
    navigate('/', { replace: true });
    setTimeout(() => {
      document.getElementById('store')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [productId]);

  useEffect(() => {
    if (product) {
      const initialColor = product.colors?.[0] || null;
      setSelectedColor(initialColor);
      setSelectedSize(product.sizes?.[0] || null);
      setMainImage(getProductImage(initialColor?.image || product.image || ''));
    }
  }, [product, productId]);

  // Escape closes the ordering dialog.
  useEffect(() => {
    if (!showContactPopup) return undefined;
    const onKey = (e) => e.key === 'Escape' && setShowContactPopup(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showContactPopup]);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper px-6">
        <div className="text-center">
          <span className="label">404</span>
          <h1 className="display mt-4">{t('Product Not Found')}</h1>
          <button type="button" onClick={handleBackToStore} className="btn btn-solid mt-8">
            {t('Return to Store')}
          </button>
        </div>
      </div>
    );
  }

  const handleShare = () => {
    const productUrl = `${window.location.origin}/store/${product.id}`;
    if (navigator.share) {
      navigator.share({ title: product.name, text: product.description, url: productUrl });
    } else {
      navigator.clipboard.writeText(productUrl);
    }
  };

  const handleWhatsAppContact = () => {
    const msg = `Hi! I'd like to order: ${product.name} — Color: ${selectedColor?.name}, Size: ${selectedSize}. Please provide ordering details.`;
    window.open(`https://wa.me/25762153272?text=${encodeURIComponent(msg)}`, '_blank');
    setShowContactPopup(false);
  };

  const handleEmailContact = () => {
    const subject = `Order Inquiry: ${product.name}`;
    const body = `Hi!\n\nI'd like to order the ${product.name}.\n\nColor: ${selectedColor?.name}\nSize: ${selectedSize}\n\nPlease provide more details.\n\nThank you!`;
    window.open(`mailto:hyphentechinfo@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
    setShowContactPopup(false);
  };

  const canonicalUrl = `https://hyphentech.africa/store/${product.id}`;
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.description,
    image: `https://hyphentech.africa${product.image.replace(/^\./, '')}`,
    offers: {
      "@type": "Offer",
      price: product.price,
      priceCurrency: product.currency,
      availability: product.inStock
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      url: canonicalUrl,
    },
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://hyphentech.africa/" },
      { "@type": "ListItem", position: 2, name: "Store", item: "https://hyphentech.africa/store" },
      { "@type": "ListItem", position: 3, name: product.name, item: canonicalUrl },
    ],
  };

  const specs = [
    { label: t('Material'), value: product.material },
    { label: t('Fit'), value: product.fit },
    { label: t('Category'), value: product.category },
    {
      label: t('Availability'),
      value: product.inStock ? t('In Stock') : t('Out of Stock'),
    },
  ];

  return (
    <div className="app min-h-screen bg-paper">
      <Helmet>
        <title>{`${product.name} - Hyphen Tech Store`}</title>
        <meta name="description" content={product.description} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="shell py-12 md:py-16 flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={handleBackToStore}
          className="link-underline text-sm font-medium"
        >
          <HiArrowLeft aria-hidden />
          {t('Back to Store')}
        </button>
        <button type="button" onClick={handleShare} className="link-underline text-sm font-medium">
          <HiShare aria-hidden />
          {t('Share')}
        </button>
      </div>

      <div className="shell pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">

          {/* ── Plates ── */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, ease }}
            className="lg:col-span-7"
          >
            <div className="relative bg-paper-warm aspect-square flex items-center justify-center p-10 md:p-16">
              <AnimatePresence mode="wait">
                <motion.img
                  key={mainImage}
                  src={mainImage}
                  alt={product.name}
                  className="max-w-full max-h-full object-contain"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                />
              </AnimatePresence>

              {product.featured && (
                <span className="absolute top-5 left-5 label bg-ink text-paper px-2.5 py-1.5">
                  {t('Featured')}
                </span>
              )}
              {product.discount && (
                <span className="absolute top-5 right-5 label tabular bg-gold text-ink px-2.5 py-1.5">
                  −{product.discount}%
                </span>
              )}
            </div>

            <div className="grid grid-cols-4 gap-3 mt-3">
              {product.colors?.map((color) => {
                const active = selectedColor?.name === color.name;
                return (
                  <button
                    key={color.name}
                    type="button"
                    onClick={() => {
                      setMainImage(getProductImage(color.image));
                      setSelectedColor(color);
                    }}
                    aria-pressed={active}
                    className={`relative bg-paper-warm aspect-square flex items-center justify-center p-3 border transition-colors duration-200 ${
                      active
                        ? 'border-ink'
                        : 'border-transparent hover:border-[color:var(--rule-strong)]'
                    }`}
                  >
                    <img
                      src={getProductImage(color.image)}
                      alt={color.name}
                      loading="lazy"
                      className="max-w-full max-h-full object-contain"
                    />
                  </button>
                );
              })}
            </div>
          </motion.div>

          {/* ── Record ── */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.08, ease }}
            className="lg:col-span-5"
          >
            <div className="lg:sticky lg:top-16">
              <span className="label">{t('Hyphen Merch')}</span>
              <h1 className="display mt-3">{product.name}</h1>
              <p className="prose-body mt-4">{product.description}</p>

              {/* Price */}
              <div className="mt-8 pt-6 border-t border-[color:var(--rule)] flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <span className="display-2 tabular">${product.price.toFixed(2)}</span>
                {product.originalPrice && (
                  <span className="text-base text-ink-faint line-through tabular">
                    ${product.originalPrice.toFixed(2)}
                  </span>
                )}
                <span className="label">{product.currency}</span>
              </div>
              {product.priceLocal && (
                <p className="mt-2 text-sm text-ink-soft tabular">
                  {product.priceLocal.toLocaleString()} {product.localCurrency}
                  {product.originalPriceLocal && (
                    <span className="text-ink-faint line-through ml-2">
                      {product.originalPriceLocal.toLocaleString()}
                    </span>
                  )}
                </p>
              )}

              {/* Colour */}
              <fieldset className="mt-9">
                <legend className="label mb-3">
                  {t('Color')}
                  <span className="ml-2 normal-case tracking-normal text-ink font-sans text-sm font-normal">
                    {selectedColor?.name || '—'}
                  </span>
                </legend>
                <div className="flex gap-3 flex-wrap">
                  {product.colors.map((color) => {
                    const active = selectedColor?.name === color.name;
                    return (
                      <button
                        key={color.name}
                        type="button"
                        onClick={() => {
                          setSelectedColor(color);
                          setMainImage(getProductImage(color?.image || ''));
                        }}
                        aria-pressed={active}
                        aria-label={color.name}
                        title={color.name}
                        className="relative w-11 h-11 rounded-full border-2 transition-transform duration-200 hover:scale-105"
                        style={{
                          backgroundColor: color.hex,
                          borderColor: active ? 'var(--ink)' : 'var(--rule-strong)',
                        }}
                      >
                        {active && (
                          <HiCheck
                            aria-hidden
                            className="absolute inset-0 m-auto text-sm mix-blend-difference text-white"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              {/* Size */}
              <fieldset className="mt-8">
                <legend className="label mb-3">
                  {t('Size')}
                  <span className="ml-2 normal-case tracking-normal text-ink font-sans text-sm font-normal">
                    {selectedSize || '—'}
                  </span>
                </legend>
                <div className="flex gap-2 flex-wrap">
                  {product.sizes.map((size) => {
                    const active = selectedSize === size;
                    return (
                      <button
                        key={size}
                        type="button"
                        onClick={() => setSelectedSize(size)}
                        aria-pressed={active}
                        className={`min-w-[3rem] h-11 px-4 text-sm font-medium border transition-colors duration-200 ${
                          active
                            ? 'bg-ink text-paper border-ink'
                            : 'bg-paper text-ink-soft border-[color:var(--rule-strong)] hover:border-ink hover:text-ink'
                        }`}
                      >
                        {size}
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              {/* Specification */}
              <dl className="mt-9 border-t border-[color:var(--rule)]">
                {specs.map(({ label, value }) => (
                  <div
                    key={label}
                    className="flex items-baseline justify-between gap-4 py-3 border-b border-[color:var(--rule)]"
                  >
                    <dt className="label">{label}</dt>
                    <dd className="text-sm text-ink capitalize">{value}</dd>
                  </div>
                ))}
              </dl>

              <button
                type="button"
                onClick={() => product.inStock && setShowContactPopup(true)}
                disabled={!product.inStock}
                className="btn btn-solid w-full mt-8 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <HiMail aria-hidden />
                {product.inStock ? t('Contact to Order') : t('Out of Stock')}
              </button>

              <p className="mt-3 text-xs text-ink-faint text-center">
                {t('We will pre-fill your message with product details')}
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── Ordering dialog ── */}
      <AnimatePresence>
        {showContactPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-ink/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowContactPopup(false)}
            role="presentation"
          >
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 16 }}
              transition={{ duration: 0.25, ease }}
              className="relative bg-paper border border-[color:var(--rule-strong)] p-7 md:p-8 max-w-md w-full shadow-xl"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="order-title"
            >
              <button
                type="button"
                onClick={() => setShowContactPopup(false)}
                aria-label={t('Close')}
                className="absolute top-4 right-4 p-2 text-ink-faint hover:text-ink transition-colors"
              >
                <HiX aria-hidden className="text-xl" />
              </button>

              <span className="label">{t('Order')}</span>
              <h2 id="order-title" className="display-2 mt-3 pr-8">
                {t('Choose your channel')}
              </h2>

              <div className="mt-6 py-4 border-y border-[color:var(--rule)]">
                <p className="text-sm font-semibold text-ink">{product.name}</p>
                <p className="text-xs text-ink-soft mt-1">
                  {t('Color')}: {selectedColor?.name} · {t('Size')}: {selectedSize}
                </p>
              </div>

              <div className="flex flex-col gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleWhatsAppContact}
                  className="btn w-full bg-[#25D366] text-white hover:bg-[#1fba59]"
                >
                  <FaWhatsapp aria-hidden className="text-lg" />
                  {t('Contact via WhatsApp')}
                </button>
                <button type="button" onClick={handleEmailContact} className="btn btn-solid w-full">
                  <HiMail aria-hidden className="text-lg" />
                  {t('Contact via Email')}
                </button>
              </div>

              <p className="text-xs text-ink-faint text-center mt-5 leading-relaxed">
                {t('Your message will be pre-filled with product details for convenience.')}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductDetail;
